import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useVideoInterview } from '../hooks/videoInterviewHook';
import { Video, Clock, CheckCircle, ArrowRight, Lock } from 'lucide-react';

export default function VideoInterviewIntroPage() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const { data, isLoading, error } = useVideoInterview(token);

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-muted">
                <p className="text-red-500">Invalid URL: Token missing</p>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-muted">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1b237a]" />
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-muted">
                <div className="text-center">
                    <p className="text-red-500 mb-4">{error instanceof Error ? error.message : "Failed to load interview details"}</p>
                    <button onClick={() => window.location.reload()} className="text-[#1b237a] underline">Retry</button>
                </div>
            </div>
        );
    }

    if (data.alreadySubmitted) {
        return (
            <div className="min-h-screen bg-muted flex flex-col items-center justify-center p-4">
                <div className="max-w-md w-full bg-card rounded-2xl shadow-xl overflow-hidden">
                    <div className="flex flex-col items-center text-center py-16 px-8 gap-4">
                        <div className="rounded-full bg-slate-100 p-5">
                            <Lock className="w-14 h-14 text-slate-500" />
                        </div>
                        <h1 className="text-3xl font-bold text-[#1b237a]">
                            Video already submitted
                        </h1>
                        <p className="text-muted-foreground max-w-sm">
                            A video interview for {data.vacancyTitle}
                            {data.organizationName && <> at {data.organizationName}</>}
                            {" "}has already been uploaded for this candidate. This link can no longer be used to record.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-muted flex flex-col items-center justify-center p-4">
            <div className="max-w-2xl w-full bg-card rounded-2xl shadow-xl overflow-hidden">
                {/* Header */}
                <div className="bg-[#1b237a] p-8 text-center text-white">
                    <h1 className="text-3xl font-bold mb-2">Video Interview</h1>
                    <p className="text-blue-100 text-lg">
                        {data.vacancyTitle} at {data.organizationName}
                    </p>
                </div>

                {/* Content */}
                <div className="p-8 space-y-8">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-foreground mb-2">Welcome, {data.candidateName}!</h2>
                        <p className="text-muted-foreground">
                            We're excited to get to know you better. This video interview consists of {data.questions.length} questions.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="bg-blue-50 p-4 rounded-xl text-center">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3 text-[#1b237a]">
                                <Clock className="w-5 h-5" />
                            </div>
                            <h3 className="font-semibold text-foreground">Take Your Time</h3>
                            <p className="text-sm text-muted-foreground mt-1">Review questions before recording</p>
                        </div>
                        <div className="bg-purple-50 p-4 rounded-xl text-center">
                            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3 text-purple-700">
                                <Video className="w-5 h-5" />
                            </div>
                            <h3 className="font-semibold text-foreground">One Video</h3>
                            <p className="text-sm text-muted-foreground mt-1">Record all answers in a single take</p>
                        </div>
                        <div className="bg-green-50 p-4 rounded-xl text-center">
                            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 text-green-700">
                                <CheckCircle className="w-5 h-5" />
                            </div>
                            <h3 className="font-semibold text-foreground">Review</h3>
                            <p className="text-sm text-muted-foreground mt-1">Watch and retake if needed</p>
                        </div>
                    </div>

                    <div className="bg-muted p-6 rounded-xl border border-border">
                        <h3 className="font-semibold text-foreground mb-3">Instructions:</h3>
                        <ul className="space-y-2 text-muted-foreground text-sm list-disc pl-5">
                            <li>Ensure you are in a quiet, well-lit environment.</li>
                            <li>Check your camera and microphone permissions.</li>
                            <li>You will see all questions on the right side of the screen.</li>
                            <li>Answer all questions in the order they appear.</li>
                            <li>Once satisfied, submit your recording.</li>
                        </ul>
                    </div>

                    <div className="pt-4">
                        <button
                            onClick={() => navigate(`/video-interview/${token}/record`)}
                            className="w-full bg-[#1b237a] text-white py-4 rounded-xl font-bold text-lg hover:bg-[#1a2a8f] transition-all transform hover:-translate-y-0.5 shadow-lg shadow-blue-900/20 flex items-center justify-center gap-2"
                        >
                            Start Interview
                            <ArrowRight className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
